// Dashboard — editorial home: greeting, KPIs, Copilot, datasets, activity, pending, quality.
// All figures fetched live from /api/stats/overview.

import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { KpiCard } from "@/components/primitives/KpiCard";
import { Button } from "@/components/primitives/Button";
import { Avatar } from "@/components/primitives/Avatar";
import { Icon, type IconName } from "@/lib/icons";
import { useToast } from "@/components/primitives/Toast";
import { api } from "@/lib/api";

interface OverviewData {
  totals: { records: number; domains: number; pendingApprovals: number; qualityScore: number; pendingDelta: number };
  perDomainQuality: Array<{ domainId: string; score: number; count: number }>;
  datasets: Array<{ id: string; name: string; icon: string; owner: string; count: number; updatedAt: string }>;
  activity: Array<{ id: string; actor: string; action: string; target: string; at: string }>;
  pending: Array<{ id: string; domainId: string; recordId: string; summary: string; maker: string; priority: string; at: string }>;
}

const DOMAIN_ICON: Record<string, IconName> = {
  instruments: "Database", counterparties: "User", currencies: "Globe",
  countries: "Globe", industries: "Folder", products: "Folder",
  "cost-centers": "Folder", "org-units": "Folder",
  "account-types": "Tag", "risk-ratings": "Quality",
};

function fmt(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n >= 10_000 ? 0 : 1)}K`;
  return n.toLocaleString();
}

function timeAgo(iso?: string): string {
  if (!iso) return "—";
  const d = new Date(iso).getTime();
  if (!d) return "—";
  const sec = Math.max(1, Math.floor((Date.now() - d) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString();
}

function actionVerb(action: string): string {
  const last = action.split(".").pop() ?? action;
  return last;
}

export function Dashboard() {
  const toast = useToast();
  const [data, setData] = useState<OverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [copilotDismissed, setCopilotDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let firstLoad = true;
    const load = () => {
      api.stats.overview()
        .then(d => { if (!cancelled) { setData(d); setLoading(false); } })
        .catch(e => {
          if (!cancelled && firstLoad) {
            setLoading(false);
            toast(e instanceof Error ? `Stats failed: ${e.message}` : "Stats failed", "error");
          }
        })
        .finally(() => { firstLoad = false; });
    };
    load();
    const id = window.setInterval(load, 20_000);
    const onFocus = () => load();
    const onMutation = () => load();
    window.addEventListener("focus", onFocus);
    window.addEventListener("aidan:mutation", onMutation);
    return () => {
      cancelled = true;
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("aidan:mutation", onMutation);
    };
  }, [toast]);

  const totals = data?.totals;
  const pendingDelta = totals?.pendingDelta ?? 0;
  const onApplyCopilot = () => toast("Copilot queued mapping refreshes — review in Approvals", "ok");
  const onDismissCopilot = () => { setCopilotDismissed(true); toast("Suggestion dismissed", "info"); };

  const kpis = [
    {
      eyebrow: "RECORDS UNDER MGMT",
      label: "Total records",
      value: loading ? "—" : fmt(totals?.records ?? 0),
      delta: loading ? "" : `${totals?.records ?? 0} rows in D1`,
      deltaTone: "ok" as const,
      sparkline: [80, 84, 82, 87, 90, 89, 94, 96, 98, 102, 108, 112, 118, 124],
      tone: "primary" as const,
      icon: <Icon.Database size={16} />,
    },
    {
      eyebrow: "DOMAINS",
      label: "Active domains",
      value: loading ? "—" : `${totals?.domains ?? 0}`,
      delta: "Live from D1",
      deltaTone: "info" as const,
      sparkline: [8, 9, 9, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10, 10],
      tone: "secondary" as const,
      icon: <Icon.Folder size={16} />,
    },
    {
      eyebrow: "APPROVALS",
      label: "Pending approvals",
      value: loading ? "—" : `${totals?.pendingApprovals ?? 0}`,
      delta: loading
        ? ""
        : pendingDelta > 0
          ? `+${pendingDelta} stale > 24h`
          : pendingDelta < 0
            ? `${pendingDelta} cleared since yesterday`
            : "All fresh < 24h",
      deltaTone: (pendingDelta > 0 ? "warn" : "ok") as "warn" | "ok",
      sparkline: [22, 19, 21, 18, 20, 17, 15, 13, 14, 16, 15, 14, 13, totals?.pendingApprovals ?? 12],
      tone: "tertiary" as const,
      icon: <Icon.Approve size={16} />,
    },
    {
      eyebrow: "DATA QUALITY",
      label: "Quality score",
      value: loading ? "—" : `${(totals?.qualityScore ?? 0).toFixed(1)}%`,
      delta: "Mean across all records",
      deltaTone: "ok" as const,
      sparkline: [95, 95.5, 96, 96.3, 96.8, 97, 97.4, 97.5, 97.6, 97.9, 98.1, 98.2, 98.3, totals?.qualityScore ?? 98],
      icon: <Icon.Quality size={16} />,
    },
  ];

  const datasets = data?.datasets ?? [];
  const activity = data?.activity ?? [];
  const pending = data?.pending ?? [];
  const quality = data?.perDomainQuality ?? [];

  return (
    <div className="lex-page">
      <header className="lex-page__head">
        <span className="lex-eyebrow">GOOD AFTERNOON, OLIVIA</span>
        <h1 className="lex-display">
          Your reference data, <span className="lex-display__em">in good hands.</span>
        </h1>
        <p className="lex-page__sub">
          <span style={{ fontVariantNumeric: "tabular-nums" }}>{loading ? "—" : fmt(totals?.records ?? 0)}</span>{" "}
          records under management across {totals?.domains ?? 0} domains.
        </p>
      </header>

      <section className="lex-kpis" aria-label="Key metrics">
        {kpis.map(k => (
          <KpiCard
            key={k.label}
            eyebrow={k.eyebrow}
            label={k.label}
            value={k.value}
            delta={k.delta}
            deltaTone={k.deltaTone}
            sparklineValues={k.sparkline}
            tone={k.tone}
            icon={k.icon}
          />
        ))}
      </section>

      <div className="lex-dash-grid">
        <article className="lex-copilot" aria-label="Copilot suggestions" hidden={copilotDismissed}>
          <span className="lex-copilot__blob" aria-hidden="true" />
          <span className="lex-eyebrow lex-eyebrow--on-dark">COPILOT · LIVE</span>
          <h2 className="lex-copilot__headline">
            I noticed <em>{totals?.pendingApprovals ?? 0} pending approvals</em>. Want me to triage them?
          </h2>
          <p className="lex-copilot__body">
            I&apos;ll group by priority, surface high-impact changes, and draft suggested decisions. You stay in control of the final call.
          </p>
          <ol className="lex-copilot__suggestions">
            <li><span className="lex-copilot__num" data-tone="tertiary">1</span><span>Triage {totals?.pendingApprovals ?? 0} pending approvals</span></li>
            <li><span className="lex-copilot__num" data-tone="secondary">2</span><span>Refresh stale mappings across {datasets.length} datasets</span></li>
            <li><span className="lex-copilot__num">3</span><span>Run quality rules across all domains</span></li>
          </ol>
          <div className="lex-copilot__cta">
            <Button tone="tertiary" variant="filled" leadingIcon={<Icon.Sparkle size={16} />} onClick={onApplyCopilot}>Apply suggestion 1</Button>
            <Button variant="text" tone="neutral" onClick={onDismissCopilot}>Dismiss</Button>
          </div>
        </article>

        <section className="lex-datasets card">
          <header className="lex-section-head">
            <h2>Datasets</h2>
            <Link to="/browse/instruments" className="lex-link-arrow">Browse all <Icon.ChevronRight size={14} /></Link>
          </header>
          <ul className="lex-datasets__list">
            {datasets.map(d => {
              const Glyph = Icon[DOMAIN_ICON[d.id] ?? "Database"];
              return (
                <li key={d.id}>
                  <Link to={`/browse/${d.id}`} className="lex-datasets__row">
                    <span className="lex-datasets__icon" aria-hidden="true"><Glyph size={18} /></span>
                    <span className="lex-datasets__main">
                      <span className="lex-datasets__name">{d.name}</span>
                      <span className="lex-datasets__meta">{d.owner ?? "—"}</span>
                    </span>
                    <span className="lex-datasets__count" style={{ fontVariantNumeric: "tabular-nums" }}>{fmt(d.count)}</span>
                    <span className="lex-datasets__fresh">Updated {timeAgo(d.updatedAt)}</span>
                  </Link>
                </li>
              );
            })}
            {!loading && datasets.length === 0 && (
              <li style={{ padding: 16, color: "var(--md-on-surface-var)" }}>No datasets yet.</li>
            )}
          </ul>
        </section>

        <section className="lex-activity card">
          <header className="lex-section-head">
            <h2>Recent activity</h2>
            <Link to="/audit" className="lex-link-arrow">Open audit log <Icon.ChevronRight size={14} /></Link>
          </header>
          <ul className="lex-activity__list">
            {activity.map((a, i) => (
              <li key={a.id ?? i}>
                <Avatar name={a.actor} size={28} tone={i % 2 === 0 ? "secondary" : "tertiary"} />
                <div>
                  <div className="lex-activity__line">
                    <strong style={{ fontWeight: 500 }}>{a.actor}</strong> {actionVerb(a.action)} <em>{a.target}</em>
                  </div>
                  <div className="lex-activity__meta">{timeAgo(a.at)}</div>
                </div>
              </li>
            ))}
            {!loading && activity.length === 0 && (
              <li style={{ padding: 16, color: "var(--md-on-surface-var)" }}>No recent activity.</li>
            )}
          </ul>
        </section>

        <section className="lex-pending card">
          <header className="lex-section-head">
            <h2>Pending approvals</h2>
            <Link to="/approvals" className="lex-link-arrow">Review all <Icon.ChevronRight size={14} /></Link>
          </header>
          <ul className="lex-pending__list">
            {pending.map(p => (
              <li key={p.id}>
                <span className="lex-pending__id">{p.id}</span>
                <div>
                  <div>{p.summary}</div>
                  <div className="lex-pending__maker">by {p.maker}</div>
                </div>
                <span className="lex-chip" data-tone={p.priority === "critical" || p.priority === "high" ? "error" : p.priority === "med" ? "tertiary" : "secondary"} data-size="sm">
                  {p.priority}
                </span>
                <Link to={`/approvals/${p.id}`} className="lex-link-arrow">Open</Link>
              </li>
            ))}
            {!loading && pending.length === 0 && (
              <li style={{ padding: 16, color: "var(--md-on-surface-var)" }}>No pending approvals.</li>
            )}
          </ul>
        </section>

        <section className="lex-quality card">
          <header className="lex-section-head">
            <h2>Quality scorecard</h2>
            <Link to="/quality" className="lex-link-arrow">View rules <Icon.ChevronRight size={14} /></Link>
          </header>
          <div className="lex-quality__grid">
            {quality.map(q => {
              const tone = q.score >= 95 ? undefined : q.score >= 85 ? ("tertiary" as const) : ("error" as const);
              return (
                <div key={q.domainId} className="lex-quality__bar">
                  <div className="lex-quality__row">
                    <span>{q.domainId}</span>
                    <span style={{ fontVariantNumeric: "tabular-nums" }}>{q.score.toFixed(1)}%</span>
                  </div>
                  <div className="lex-quality__track" data-tone={tone}>
                    <div className="lex-quality__fill" style={{ width: `${Math.min(100, q.score)}%` }} />
                  </div>
                </div>
              );
            })}
            {!loading && quality.length === 0 && (
              <div style={{ padding: 16, color: "var(--md-on-surface-var)" }}>No quality data.</div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

export default Dashboard;
